<template>
  <footer ref="footer" :id="random_id" :data-testid="props.dataTestid">
    <TsNewsletter
      v-if="!isDesktop"
      :heading="
        useTranslation(
          'newsLetterHeading',
          'Altijd op de hoogte van onze acties'
        )
      "
      :description="
        useTranslation(
          'newsletterDescription',
          'Schrijf je in voor de nieuwsbrief en mis nooit meer iets. Ontvang direct 10% korting na registratie.'
        )
      "
      append-class="md:hidden py-6 px-4"
    />
    <div class="mx-auto max-w-screen-xl px-4 py-6 max-md:bg-[#F0F8FF]">
      <TsRow
        class="max-lg:flex-col justify-between lg:bg-white lg:rounded-md lg:border lg:border-[#DCEDFD] lg:my-4"
      >
        <TsColumn dataTestid="column-cta">
          <TsCardCta
            data-testid="footer-cta"
            :content="
              useTranslation(
                'downloadApp',
                'Download de app en ontvang 5% KORTING'
              )
            "
            append-class="md:py-2"
          />
        </TsColumn>

        <TsColumn v-if="isDesktop" class="grow-0" dataTestid="column-divider">
          <TsDivider layout="vertical" />
        </TsColumn>

        <TsColumn
          class="max-md:mt-2 flex items-center justify-center"
          dataTestid="column-secure-payment"
        >
          <TsRow class="items-center max-sm:flex-col mx-0 my-auto">
            <TsTypography
              as="p"
              variant="base"
              data-testid="footer-para"
              class="max-sm:mb-3 max-sm:mr-0 mr-3 sm:mr-4"
              append-class="mb-0 shrink font-bold text-idle-black"
            >
              {{ useTranslation("securePayment", "Veilige betaling") }}
            </TsTypography>

            <TsRow
              class="justify-start lg:justify-center gap-2 sm:gap-3.5 mx-0 overflow-auto hide-scroll-bar"
              isMobile
            >
              <template v-for="option in payment_options">
                <TsImage
                  v-if="option && option.image"
                  :key="option.alt"
                  class="h-6 max-w-full"
                  :src="option.image"
                  :alt="option.alt"
                  :width="option.width"
                  :height="option.height"
                  loading="lazy"
                />
              </template>
            </TsRow>
          </TsRow>
        </TsColumn>
      </TsRow>
    </div>

    <div
      class="md:hidden bg-primary py-6 px-4"
      v-if="footerContentLinks && footerContentLinks.length > 0 && !isDesktop"
    >
      <NuxtLinkWithLocale
        to="/"
        @click="handleLogoClickEvent"
        class="block max-w-max mx-auto"
      >
        <TsImage
          src="/images/logos/toolstation-logo-halo.svg"
          alt="Toolstation"
          class="w-56 mx-auto mb-6 mt-2 cursor-pointer"
          width="224"
          height="55"
          loading="lazy"
        />
      </NuxtLinkWithLocale>
      <div class="mb-8">
        <TsAccordion dataTestid="column-accordion" append-class="mb-0">
          <template
            v-for="(tab, index) in footerContentLinks"
            :key="tab.data.heading.name"
          >
            <TsAccordionTab
              v-if="tab.data.links?.length > 0"
              :heading="tab.data.heading.name"
              :data-testid="`mobile-${props.dataTestid}-navigation-tab-${index}`"
              appendClass="w-full px-0 !text-idle-white border-0 border-t-0 border-b border-b-info first:border-t-0 hover:bg-transparent"
              headingClass="text-sm font-medium px-3 border-b-0"
            >
              <ul v-if="tab.data.links?.length > 0" class="px-4">
                <li
                  v-for="(item, i) in tab.data.links"
                  :key="item.name"
                  class="py-2 text-natural-soft-white text-sm font-normal"
                >
                  <NuxtLinkWithLocale
                    :to="item?.url || ''"
                    :target="item?.openInNewTab ? '_blank' : '_self'"
                    :data-testid="`${
                      props.dataTestid + (item.url || '').replace(/\//g, '-')
                    }-mob`"
                    class="w-full"
                    @click="handleLogEvent(item)"
                  >
                    {{ item.name }}
                  </NuxtLinkWithLocale>
                </li>
              </ul>
            </TsAccordionTab>
            <NuxtLinkWithLocale
              v-else-if="tab && tab.data && tab.data.heading"
              :to="tab.data.heading.url || ''"
              class="font-medium text-sm block py-4 border-b border-info text-white pl-3"
              @click="handleLogEvent(tab.data.heading)"
            >
              {{ tab.data.heading.name }}
            </NuxtLinkWithLocale>
          </template>
        </TsAccordion>
      </div>
      <TsTypography align="center" class="!text-idle-white text-lg mb-1">
        {{ useTranslation("inLoop", "Op de hoogte blijven?") }}
      </TsTypography>

      <TsRow append-class="justify-center gap-3 mt-1.5 !mx-0" isMobile>
        <NuxtLinkWithLocale
          v-for="(item, index) in social_links.mobile"
          target="_blank"
          :key="item.name"
          :to="item.link"
          :data-testid="'mobile-' + props.dataTestid + '-social-link-' + index"
          @click="handleSocialMediaLinkClickEvent(item)"
        >
          <TsImage
            class="size-12"
            v-if="item && item.image"
            :src="item.image"
            :alt="item.name"
            loading="lazy"
          />
        </NuxtLinkWithLocale>
      </TsRow>
    </div>

    <div v-if="isDesktop" class="bg-primary px-4">
      <TsRow
        class="max-md:hidden py-8 mx-auto max-w-screen-xl min-h-max"
        isMobile
      >
        <TsColumn class="is-5 min-w-min" dataTestid="column-left">
          <NuxtLinkWithLocale
            to="/"
            @click="handleLogoClickEvent"
            class="block max-w-max cursor-pointer"
          >
            <TsImage
              class="w-56 cursor-pointer"
              src="/images/logos/toolstation-logo-halo.svg"
              alt="Tool Box"
              width="224"
              height="55"
              loading="lazy"
            />
          </NuxtLinkWithLocale>
          <TsNewsletter
            :heading="
              useTranslation(
                'newsLetterHeading',
                'Altijd op de hoogte van onze acties'
              )
            "
            :description="
              useTranslation(
                'newsletterDescription',
                'Schrijf je in voor de nieuwsbrief en mis nooit meer iets. Ontvang direct 10% korting na registratie.'
              )
            "
            append-class="py-6 max-sm:px-4"
          />

          <TsTypography
            append-class="text-center sm:text-left mt-2 mb-2 text-idle-white"
          >
            {{ useTranslation("inLoop", "Op de hoogte blijven?") }}
          </TsTypography>

          <TsRow
            append-class="max-sm:justify-center gap-2.5 mt-2 mx-0"
            isMobile
          >
            <NuxtLinkWithLocale
              v-for="(item, index) in social_links.mobile"
              target="_blank"
              :key="item.name"
              :to="item.link"
              :data-testid="
                'desktop-' + props.dataTestid + '-social-link-' + index
              "
              @click="handleSocialMediaLinkClickEvent(item)"
            >
              <TsImage
                class="size-10"
                v-if="item && item.image"
                :src="item.image"
                :alt="item.name"
                loading="lazy"
              />
            </NuxtLinkWithLocale>
          </TsRow>
        </TsColumn>

        <TsColumn class="grow-0" dataTestid="column-divider">
          <TsDivider
            v-if="isDesktop || isTablet"
            layout="vertical"
            append-class="before:border-[#4191DB]"
          />
        </TsColumn>

        <TsColumn dataTestid="column-right">
          <TsRow
            class="flex-row"
            isMultiline
            v-if="
              footerContentLinks && Object.keys(footerContentLinks).length > 0
            "
          >
            <template
              v-for="(item, i) in footerContentLinks.slice(0, 3)"
              :key="item?.id"
            >
              <TsColumn dataTestid="column-one" v-if="item.data.links">
                <TsTypography
                  append-class="font-bold !mb-4 !text-idle-white"
                  :data-testid="item.name"
                >
                  {{ item.data.heading.name }}
                </TsTypography>
                <ul class="mb-6">
                  <TsTypography
                    v-for="(link, index) in item.data.links"
                    :key="link?.name"
                    as="li"
                    variant="sm"
                    :append-class="
                      twMerge(
                        'text-idle-white',
                        index !== item.data.links.length - 1 && 'mb-2'
                      )
                    "
                  >
                    <NuxtLinkWithLocale
                      :to="link.url || ''"
                      :target="link?.openInNewTab ? '_blank' : '_self'"
                      :data-testid="`desktop-${
                        props.dataTestid + (link.url || '').replace(/\//g, '-')
                      }`"
                      @click="handleLogEvent(link)"
                    >
                      {{ link?.name }}
                    </NuxtLinkWithLocale>
                  </TsTypography>
                </ul>
                <template v-for="headingLink in footerContentLinks.slice(3)">
                  <NuxtLinkWithLocale
                    v-if="i === 2"
                    :to="headingLink.data.heading?.url || ''"
                    :target="
                      headingLink.data.heading?.openInNewTab
                        ? '_blank'
                        : '_self'
                    "
                    @click="handleLogEvent(headingLink.data.heading)"
                    class="font-bold block mt-4 text-idle-white"
                    :data-testid="
                      'desktop-' + props.dataTestid + '-Advice/Blogs/News'
                    "
                  >
                    {{ headingLink.data.heading?.name }}
                  </NuxtLinkWithLocale>
                </template>
              </TsColumn>
            </template>
          </TsRow>
        </TsColumn>
      </TsRow>
    </div>

    <div class="bg-primary-hover">
      <TsRow
        class="flex-col items-center text-idle-white py-6 px-4 md:justify-between md:flex-row mx-auto max-w-screen-xl"
      >
        <TsTypography
          align="center"
          append-class="mr-4 leading-relaxed text-idle-white mb-0"
        >
          {{ useTranslation("copyright", "Copyright") }}
          <NuxtLinkWithLocale to="/">Toolstation</NuxtLinkWithLocale>
          {{ currentYear }}.
          {{ useTranslation("chamberOfCommerce", "KvK-nummer:") }}
          63449595<br />{{ useTranslation("vatNumber", "Btw-nummer") }}
          NL8552.40.854.B01
          <small
            >{{ useTranslation("version", "version") }}:
            <span class="cursor-pointer underline hover:no-underline">{{
              config.public.version
            }}</span>
          </small>
        </TsTypography>

        <ul
          class="relative flex flex-wrap justify-center items-center shrink-0 max-md:mt-2"
        >
          <li class="flex px-4 max-md:mt-2 max-md:border-r-2">
            <NuxtLinkWithLocale
              to="/content/privacy"
              :data-testid="'desktop-' + props.dataTestid + '-privacy-policy'"
            >
              <TsTypography align="center" append-class="text-idle-white mb-0">
                <span
                  v-html="useTranslation('privacyPolicy', 'Privacybeleid')"
                ></span>
              </TsTypography>
            </NuxtLinkWithLocale>
          </li>
          <li class="flex px-4 max-md:mt-2 sm:max-md:border-r-2">
            <NuxtLinkWithLocale
              to="/content/cookies"
              :data-testid="'desktop-' + props.dataTestid + '-cookie-policy'"
            >
              <TsTypography append-class="text-idle-white mb-0">
                <span
                  v-html="useTranslation('cookiePolicy', 'Cookiebeleid')"
                ></span>
              </TsTypography>
            </NuxtLinkWithLocale>
          </li>
        </ul>
      </TsRow>
    </div>
  </footer>
</template>

<script setup lang="ts">
import { useTranslation } from "~/composables/useTranslation";
import { useRandomUUID } from "~/composables/useRandomUUID";
import { twMerge } from "tailwind-merge";
import { onBeforeUnmount, onMounted, ref } from "vue";
const nuxtApp = useNuxtApp();
const config = useRuntimeConfig();

// Track component mount state to prevent operations after unmount
const isMounted = ref(false);
// Store timeout IDs for proper cleanup
const timeoutIds = ref<number[]>([]);

// Define async components with error handling and proper loading states
const TsNewsletter = defineAsyncComponent({
  loader: () => import("~/components/organisms/TsNewsletter.vue"),
  delay: 200, // delay before showing loading component
  timeout: 5000, // timeout before showing error component
  suspensible: false,
  errorComponent: {
    template: "<div>Error loading Newsletter component</div>",
  },
});

const TsCardCta = defineAsyncComponent({
  loader: () => import("~/components/organisms/TsCardCta.vue"),
  delay: 200,
  timeout: 5000,
  suspensible: false,
  errorComponent: {
    template: "<div>Error loading CardCta component</div>",
  },
});

const TsAccordion = defineAsyncComponent({
  loader: () => import("~/components/atoms/TsAccordion.vue"),
  delay: 200,
  timeout: 5000,
  errorComponent: {
    template: "<div>Error loading Accordion component</div>",
  },
});

const TsAccordionTab = defineAsyncComponent({
  loader: () => import("~/components/atoms/TsAccordionTab.vue"),
  delay: 200,
  timeout: 5000,
  errorComponent: {
    template: "<div>Error loading AccordionTab component</div>",
  },
});

const currentYear = new Date().getFullYear();

const random_id = ref("");
const { isDesktop, isTablet } = useDevice();

// Create a debounce utility to reduce CPU spikes from frequent function calls
const debounce = (fn: Function, wait = 300) => {
  let timeoutId: number;
  return function (...args: any[]) {
    clearTimeout(timeoutId);
    timeoutId = window.setTimeout(() => {
      if (isMounted.value) {
        // Only execute if component is still mounted
        fn.apply(null, args);
      }
    }, wait);
    timeoutIds.value.push(timeoutId);
  };
};

// Implement proper lifecycle hooks
onMounted(() => {
  isMounted.value = true;
  random_id.value = useRandomUUID();

  if (import.meta.client) {
    // Prefetch critical components to improve performance
    Promise.all([
      import("~/components/organisms/TsNewsletter.vue"),
      import("~/components/organisms/TsCardCta.vue"),
    ]).catch((error) => {
      console.error("Error prefetching components:", error);
    });
  }
});

const handleLogoClickEvent = debounce(() => {
  if (!isMounted.value) return;

  logEvent({
    options: {
      eventAction: "click",
      eventProperties: {
        Event: "Clicked",
      },
      eventInteraction: true,
      eventLabel: `Footer Logo Clicked`,
      eventName: "footer_link_clicks",
    },
  });
}, 300);

const handleSocialMediaLinkClickEvent = debounce((media: any) => {
  if (!isMounted.value || !media || !media.name) return;

  logEvent({
    options: {
      eventAction: "click",
      eventProperties: {
        data: {
          link: media.link,
          name: media.name,
        },
      },
      eventInteraction: true,
      eventLabel: `Social Media Link : ${media.name} Clicked`,
      eventName: "footer_social_media_clicks",
    },
  });
}, 300);

type Props = {
  dataTestid?: string;
  dataFetchKey?: string;
};

const props = withDefaults(defineProps<Props>(), {
  dataFetchKey: "footer-links",
});

const social_links = {
  mobile: [
    {
      link: "https://www.facebook.com/ToolstationNederland",
      image: "/icons/mobile-facebook.svg",
      name: "Facebook",
    },
    {
      link: "https://www.instagram.com/toolstation_nl/",
      image: "/icons/mobile-instagram.svg",
      name: "Instagram",
    },
    {
      link: "http://www.youtube.com/channel/UCT5P5fy539NUKmHX8gOOpAQ",
      image: "/icons/mobile-youtube.svg",
      name: "YouTube",
    },
    {
      link: "https://www.linkedin.com/company/toolstation-nl",
      image: "/icons/mobile-linkedin.svg",
      name: "LinkedIn",
    },
  ],
  desktop: [
    {
      link: "/",
      image: "/icons/desktop-instagram.svg",
      name: "Instagram",
    },
    {
      link: "/",
      image: "/icons/desktop-facebook.svg",
      name: "Facebook",
    },
    {
      link: "/",
      image: "/icons/desktop-twitter.svg",
      name: "Twitter",
    },
    {
      link: "/",
      image: "/icons/desktop-youtube.svg",
      name: "YouTube",
    },
    {
      link: "/",
      image: "/icons/desktop-linkedin.svg",
      name: "LinkedIn",
    },
  ],
};

const payment_options = [
  {
    image: "/icons/ic_outline-apple.svg",
    alt: "apple pay",
    width: "24",
    height: "24",
  },
  {
    image: "/icons/paypal.svg",
    alt: "paypal",
    width: "21",
    height: "24",
  },
  {
    image: "/icons/american-express.svg",
    alt: "american express",
    width: "42",
    height: "24",
  },
  {
    image: "/icons/i-deal.svg",
    alt: "iDeal",
    width: "25",
    height: "24",
  },
  {
    image: "/icons/master-card.svg",
    alt: "master card",
    width: "29",
    height: "24",
  },
  {
    image: "/icons/google-pay.svg",
    alt: "google pay",
    width: "25",
    height: "24",
  },
  {
    image: "/icons/image-1.png",
    alt: "pro card",
    width: "40",
    height: "28",
  },
  {
    image: "/icons/visa.svg",
    alt: "visa",
    width: "40",
    height: "24",
  },
];

// Lazy data fetching for footer links from CMS
const { data: footerContentLinks, clear: clearFooterAsyncHandler } =
  await useAsyncData(
    props.dataFetchKey,
    () =>
      fetchFooterLinks(
        config.public.builderApiKey,
        { locale: nuxtApp.$i18n.locale.value },
        nuxtApp
      ),
    {
      // Refresh the data if cached links are not found
      lazy: true,
      dedupe: "cancel",
      // Trigger the refetch whenever the locale changes
      watch: [nuxtApp.$i18n.locale],
    }
  );

async function fetchFooterLinks(
  apiKey: any,
  opts: object,
  nuxtApp: any = null,
  redisCacheBurst = false
) {
  const { getFooterLinks } = await import(
    "@/services-optimized/builderService"
  );
  return await getFooterLinks(apiKey, opts, nuxtApp, redisCacheBurst);
}

const handleLogEvent = debounce((FooterData: any) => {
  if (!isMounted.value || !FooterData) return;

  logEvent({
    options: {
      eventAction: "click",
      eventProperties: {
        data: FooterData,
      },
      eventInteraction: true,
      eventLabel: `Footer Link : ${FooterData.name || ""} Clicked`,
      eventName: "footer_link_clicks",
    },
  });
}, 300);

// Comprehensive cleanup to prevent memory leaks
onBeforeUnmount(() => {
  // Set mounted state to false to prevent operations after unmount
  isMounted.value = false;

  // Clear all timeout IDs
  timeoutIds.value.forEach((id) => {
    try {
      clearTimeout(id);
    } catch (error) {
      console.error("Error clearing timeout:", error);
    }
  });
  timeoutIds.value = [];

  // Clear the useAsyncData handler for footer links
  clearFooterAsyncHandler();
});
</script>
